import { useMutation } from '@tanstack/react-query';
import { debounce, isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type UseUndoableSaveOptions<T> = {
  save: (value: T) => Promise<unknown>;
  apply?: (value: T) => void;
  onError?: () => void;
  debounceMs?: number;
  undoVisibleMs?: number;
};

type UndoableSaveBatch<T> = { previous: T; next: T; isUndo?: boolean };

export const useUndoableSave = <T>({
  save,
  apply,
  onError,
  debounceMs = 700,
  undoVisibleMs = 5000,
}: UseUndoableSaveOptions<T>) => {
  const batchRef = useRef<UndoableSaveBatch<T> | undefined>(undefined);
  const savedRef = useRef<T | undefined>(undefined);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [undoValue, setUndoValue] = useState<T | undefined>(undefined);
  const [isScheduled, setIsScheduled] = useState(false);

  const track = useCallback(
    (previous: T | undefined, next: T) => {
      savedRef.current = next;
      clearTimeout(hideTimerRef.current);
      setUndoValue(previous);
      if (previous !== undefined) {
        hideTimerRef.current = setTimeout(() => setUndoValue(undefined), undoVisibleMs);
      }
    },
    [undoVisibleMs],
  );

  const { mutate, reset, isPending } = useMutation({
    mutationFn: ({ next }: UndoableSaveBatch<T>) => save(next),
    onMutate: ({ next }) => apply?.(next),
    onSuccess: (_, { previous, next, isUndo }) => track(isUndo ? undefined : previous, next),
    onError: (_, { previous }) => {
      apply?.(previous);
      onError?.();
    },
  });

  const commit = useMemo(
    () =>
      debounce(() => {
        const batch = batchRef.current;
        batchRef.current = undefined;
        setIsScheduled(false);
        if (batch && !isEqual(batch.previous, batch.next)) {
          mutate(batch);
        }
      }, debounceMs),
    [mutate, debounceMs],
  );

  useEffect(
    () => () => {
      commit.flush();
      clearTimeout(hideTimerRef.current);
    },
    [commit],
  );

  const schedule = useCallback(
    (previous: T, next: T) => {
      batchRef.current = { previous: batchRef.current?.previous ?? previous, next };
      setIsScheduled(true);
      commit();
    },
    [commit],
  );

  const undo = useCallback(() => {
    const current = savedRef.current;
    if (undoValue === undefined || current === undefined) {
      return;
    }

    clearTimeout(hideTimerRef.current);
    setUndoValue(undefined);
    mutate({ previous: current, next: undoValue, isUndo: true });
  }, [mutate, undoValue]);

  const clear = useCallback(() => {
    commit.cancel();
    clearTimeout(hideTimerRef.current);
    batchRef.current = undefined;
    savedRef.current = undefined;
    setUndoValue(undefined);
    setIsScheduled(false);
    reset();
  }, [commit, reset]);

  return {
    schedule,
    track,
    flush: commit.flush,
    undo,
    clear,
    isScheduled,
    isSaving: isScheduled || isPending,
    canUndo: undoValue !== undefined,
  };
};
